import type { TutorialStep } from "../Constants/tutorialSteps";
import { getTutorialNarration } from "../Constants/tutorialNarration";
import {
  estimateSpeechDurationMs,
  type TutorialSpeechRate,
  TUTORIAL_SPEECH_END_BEAT_MS,
} from "./tutorialSpeech";

const AUTO_PLAY_STORAGE_KEY = "intentico:tutorialAutoPlay";

type VoiceDurationOptions = {
  voiceEnabled?: boolean;
  speechRate?: TutorialSpeechRate;
};

/** Skim-friendly dwell when voice is muted: enough to read, not dragging. */
export function getTutorialAutoPlayDurationMs(step: TutorialStep): number {
  const isCompact = step.tooltipSize === "compact";
  const bulletCount = Math.min(
    step.bullets.length,
    isCompact ? 2 : 3,
  );
  const textLen =
    step.title.length + step.subtitle.length + step.body.length;
  const fromText = Math.min(3200, Math.round(textLen / 32) * 1000);
  const raw = 4200 + bulletCount * 850 + fromText;
  return Math.min(9000, Math.max(5000, raw));
}

/** Auto-play dwell for a step when narration is expected to play. */
export function getTutorialStepAutoPlayDurationMs(
  step: TutorialStep,
  options?: VoiceDurationOptions,
): number {
  if (options?.voiceEnabled) {
    const narration = getTutorialNarration(step.id);
    if (narration) {
      return (
        estimateSpeechDurationMs(narration, options.speechRate ?? 1) +
        TUTORIAL_SPEECH_END_BEAT_MS
      );
    }
  }
  return getTutorialAutoPlayDurationMs(step);
}

/** Sum of auto-play dwells for a section path. */
export function getTutorialSectionAutoPlayDurationMs(
  steps: TutorialStep[],
  options?: VoiceDurationOptions,
): number {
  return steps.reduce(
    (total, step) => total + getTutorialStepAutoPlayDurationMs(step, options),
    0,
  );
}

/** Human label for section picker estimates, e.g. "~2 min". */
export function formatTutorialDurationEstimate(durationMs: number): string {
  const totalSeconds = Math.max(1, Math.round(durationMs / 1000));
  if (totalSeconds < 60) {
    const rounded = Math.max(15, Math.round(totalSeconds / 5) * 5);
    return `~${rounded} sec`;
  }
  const minutes = totalSeconds / 60;
  if (minutes < 10) {
    const tenths = Math.round(minutes * 2) / 2;
    const label =
      tenths % 1 === 0 ? String(tenths) : tenths.toFixed(1);
    return `~${label} min`;
  }
  return `~${Math.round(minutes)} min`;
}

export function readTutorialAutoPlayPreference(): boolean {
  try {
    return sessionStorage.getItem(AUTO_PLAY_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function writeTutorialAutoPlayPreference(enabled: boolean): void {
  try {
    sessionStorage.setItem(AUTO_PLAY_STORAGE_KEY, enabled ? "1" : "0");
  } catch {
    // sessionStorage may be unavailable
  }
}
